'use client';

import classNames from 'classnames';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import Image from '@alltrails/core/components/Image';
import DropdownMenu from '@alltrails/denali/components/DropdownMenu';
import IconButton from '@alltrails/denali/components/IconButton';
import Add from '@alltrails/denali/icons/Add';
import Edit from '@alltrails/denali/icons/Edit';
import Hidden from '@alltrails/denali/icons/Hidden';
import LinkIcon from '@alltrails/denali/icons/Link';
import Merge from '@alltrails/denali/icons/Merge';
import Overflow from '@alltrails/denali/icons/Overflow';
import Remove from '@alltrails/denali/icons/Remove';
import type { DropdownMenuOption } from '@alltrails/denali/types';
import type { InternalCardProps } from '../../types';
import CardLink from '../CardLink';
import CompletionIcon from '../CompletionIcon';
import HeartButton from '../HeartButton';
import styles from './styles/styles.module.scss';

type AdminCTAType = {
  label: string;
  id: string;
  onClick: () => void;
};

export type SmallCardProps = InternalCardProps & {
  photo: string;
  adminCTAs?: AdminCTAType[];
  role?: string;
  ariaLabel?: string;
};

const SmallCard = ({
  className,
  name,
  href,
  openInNewTab,
  onCardClick,
  onCardMouseEnter,
  onCardMouseLeave,
  metadata,
  onFavoriteClick,
  isFavorite = false,
  isCompleted = false,
  isVerified = false,
  photo,
  adminCTAs,
  onCompletionBadgeClick,
  blockNavigation,
  lazyLoadImages,
  role,
  ariaLabel
}: SmallCardProps) => {
  const imgLoading = lazyLoadImages ? 'lazy' : 'eager';
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const intl = useIntl();

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setIsDropdownOpen(true);
  };

  const dropdownItemIcon = (id: string) => {
    switch (id) {
      case 'edit':
        return { Component: Edit };
      case 'merge':
        return { Component: Merge };
      case 'attach-to-trail':
        return { Component: LinkIcon };
      case 'add-as-new-trail':
        return { Component: Add };
      case 'remove':
        return { Component: Remove };
      default:
        return { Component: Hidden };
    }
  };

  const dropdownMenuOptions: DropdownMenuOption[] = adminCTAs?.length
    ? adminCTAs.map(option => ({
        testId: `${option.label}_dropdown`,
        title: option.label,
        onClick: option.onClick,
        id: option.id,
        leadingIcon: { icon: dropdownItemIcon(option.id) }
      }))
    : [];

  const adminActionsElement =
    adminCTAs && adminCTAs.length ? (
      <div className={styles.adminActions}>
        <DropdownMenu
          anchor={
            <IconButton
              icon={{ Component: Overflow }}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              title={intl.formatMessage({ defaultMessage: 'Admin actions' })}
              testId="admin-actions-button"
              className={styles.iconButton}
              variant="transparent"
              size="sm"
            />
          }
          isOpen={isDropdownOpen}
          onCloseRequest={() => setIsDropdownOpen(false)}
          options={dropdownMenuOptions}
        />
      </div>
    ) : null;

  return (
    <div
      className={classNames('xlate-none', styles.container, className)}
      onMouseEnter={onCardMouseEnter}
      onMouseLeave={onCardMouseLeave}
      title={name}
      onContextMenu={handleContextMenu}
      role={role}
      aria-label={ariaLabel}
    >
      <CardLink
        href={href}
        blockNavigation={blockNavigation}
        openInNewTab={openInNewTab}
        onClick={onCardClick}
        isOverlay
        overlayFocusStyle="border"
        testId={`${name}_OverlayLink`}
      >
        {name}
      </CardLink>
      <div className={styles.contentAndFooter}>
        <div className={styles.content}>
          <div className={styles.completed}>
            <CompletionIcon isCompleted={isCompleted} isVerified={isVerified} onClick={onCompletionBadgeClick} />
          </div>
          {adminActionsElement ||
            (onFavoriteClick && (
              <div className={styles.heart}>
                <HeartButton isSelected={isFavorite} onClick={onFavoriteClick} />
              </div>
            ))}
          <Image key={photo} src={photo} alt="" className={styles.image} loading={imgLoading} unoptimized width={0} height={0} />
          <div className={classNames(styles.metadata, { [styles.hasHeartIcon]: onFavoriteClick || adminActionsElement })}>{metadata}</div>
          {adminActionsElement && onFavoriteClick && (
            <div className={styles.bottomHeart}>
              <HeartButton isSelected={isFavorite} onClick={onFavoriteClick} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmallCard;
